import { isSafeFieldName } from '../../../../utils/validation.utils';
import {
  type DataProviderSet,
  DataType,
  isBoxDataPoint,
} from '../../../DataProvider/types';
import {
  createEntityIdentifier,
  type ExtractionOptions,
  entityKey,
  extractDatasetValues,
  matchesEntity,
  normalizeDataset,
  safeNumber,
} from './extractDatasetValues';

const AggregationFunctions = [
  'first',
  'last',
  'min',
  'max',
  'sum',
  'avg',
  'median',
  'time_weighted_avg',
] as const;

export type AggregationFunction = (typeof AggregationFunctions)[number];

export const isValidAggregationFunction = (
  value: unknown
): value is AggregationFunction => {
  return AggregationFunctions.includes(value as AggregationFunction);
};

/**
 * Shared reduce over numeric values. Filters out null/non-finite, then applies
 * the given strategy. Used by PERCENTILE_RANK, VALUE, and aggregation helpers.
 */
export const reduceNumericValues = (
  values: (number | null)[],
  reduce: AggregationFunction
): number | null => {
  const numericValues = values.filter(
    (v): v is number => v !== null && Number.isFinite(v)
  );
  if (numericValues.length === 0) return null;
  switch (reduce) {
    case 'first':
      return numericValues[0] ?? null;
    case 'last':
      return numericValues[numericValues.length - 1] ?? null;
    case 'min':
      return Math.min(...numericValues);
    case 'max':
      return Math.max(...numericValues);
    case 'sum':
      return numericValues.reduce((acc, v) => acc + v, 0);
    case 'avg':
      return (
        numericValues.reduce((acc, v) => acc + v, 0) / numericValues.length
      );
    case 'median': {
      const sorted = [...numericValues].sort((a, b) => a - b);
      const len = sorted.length;

      if (len === 0) return null;

      const mid = Math.floor(len / 2);

      if (len % 2 === 0) {
        const left = sorted[mid - 1];
        const right = sorted[mid];

        if (left !== undefined && right !== undefined) {
          return (left + right) / 2;
        }
        return null;
      }

      const value = sorted[mid];
      return value !== undefined ? value : null;
    }
    case 'time_weighted_avg':
      return null; // handled in calculateTimeWeightedScore as it needs (date, value) pairs
    default:
      return null;
  }
};

type EntityValue = { entity: string; id?: string; values: number[] };

/**
 * Extracts entity-value pairs from a dataset for ranking/arg operations.
 * This is used by ARG_MAX, ARG_MIN, and RANK helpers.
 */
export const entityValuesFromDataset = ({
  dataset,
  extractionOptions,
}: {
  dataset: DataProviderSet;
  extractionOptions: ExtractionOptions;
}): EntityValue[] => {
  const normalizedPoints = normalizeDataset(dataset);

  let filtered = normalizedPoints;

  if (extractionOptions.segment) {
    filtered = filtered.filter(
      (point) => point.segment && point.segment === extractionOptions.segment
    );
  }

  if (dataset.type === DataType.TABLE_ROWS) {
    const labelField =
      extractionOptions.labelField ??
      (extractionOptions.whereField &&
      isSafeFieldName(extractionOptions.whereField)
        ? extractionOptions.whereField
        : undefined);

    if (!labelField || !isSafeFieldName(labelField)) return [];

    const entityMap = new Map<string, EntityValue>();

    filtered.forEach((point) => {
      const entity = String(point.attributes[labelField]);
      if (!entity) return;

      const rowId =
        (point.attributes.id as string | null | undefined) ?? point.id ?? null;
      const resolved = createEntityIdentifier(entity, rowId);
      const key = entityKey(resolved);

      if (!entityMap.has(key)) {
        entityMap.set(key, {
          entity: resolved.entity,
          id: resolved.id,
          values: [],
        });
      }

      let value: number | null = point.value;
      if (extractionOptions.field && isSafeFieldName(extractionOptions.field)) {
        value = safeNumber(point.attributes[extractionOptions.field]);
      }

      if (value !== null && Number.isFinite(value)) {
        entityMap.get(key)?.values.push(value);
      }
    });

    return Array.from(entityMap.values());
  }

  if (dataset.type === DataType.SINGLE) {
    const singleField =
      extractionOptions.field === 'count' ||
      extractionOptions.field === 'rawValue' ||
      extractionOptions.field === 'value'
        ? extractionOptions.field
        : 'value';

    filtered = filtered.map((point) => ({
      ...point,
      value: safeNumber(point.attributes[singleField]),
    }));
  }

  if (dataset.type === DataType.BOX && extractionOptions.percentile) {
    const percentile = extractionOptions.percentile;
    filtered = filtered.map((point) => {
      const box = point.attributes.box;
      return {
        ...point,
        value: isBoxDataPoint(box) ? safeNumber(box[percentile]) : null,
      };
    });
  }

  // Group remaining points by entity key (ID or name)
  const entityMap = new Map<string, EntityValue>();

  filtered.forEach((point) => {
    if (point.value === null || !Number.isFinite(point.value)) return;

    const key = entityKey(point);

    if (!entityMap.has(key)) {
      entityMap.set(key, {
        entity: point.entity,
        id: point.id,
        values: [],
      });
    }

    entityMap.get(key)?.values.push(point.value);
  });

  return Array.from(entityMap.values());
};

type EntityMetric = { entity: string; id?: string; value: number };

/**
 * Sorts entities by value in descending order, with ties broken by entity name (ascending).
 * Used for ARG_MAX and RANK operations.
 */
const sortByValueDesc = (list: EntityMetric[]): EntityMetric[] => {
  return [...list].sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    return a.entity.localeCompare(b.entity);
  });
};

/**
 * Sorts entities by value in ascending order, with ties broken by entity name (ascending).
 * Used for ARG_MIN operations.
 */
const sortByValueAsc = (list: EntityMetric[]): EntityMetric[] => {
  return [...list].sort((a, b) => {
    if (a.value !== b.value) return a.value - b.value;
    return a.entity.localeCompare(b.entity);
  });
};

/**
 * Reduces entity values and sorts them by the specified direction.
 */
const reduceAndSortEntityValues = (
  entityValues: EntityValue[],
  reduce: AggregationFunction,
  sortDirection: 'asc' | 'desc'
): EntityMetric[] => {
  // Reduce values per entity
  const reduced: EntityMetric[] = [];

  for (const { entity, id, values } of entityValues) {
    if (values.length === 0) continue;

    const reducedValue = reduceNumericValues(values, reduce);

    if (reducedValue !== null && Number.isFinite(reducedValue)) {
      reduced.push({ entity, id, value: reducedValue });
    }
  }

  return sortDirection === 'desc'
    ? sortByValueDesc(reduced)
    : sortByValueAsc(reduced);
};

/**
 * Returns the entity label with the maximum value for a metric.
 */
export const getArgMax = (
  extractionOptions: ExtractionOptions,
  dataset: DataProviderSet,
  reduce: AggregationFunction = 'max'
): string => {
  const entityValues = entityValuesFromDataset({
    dataset,
    extractionOptions: {
      ...extractionOptions,
      entity: undefined,
      label: undefined,
    },
  });

  if (entityValues.length === 0) return '';

  const sorted = reduceAndSortEntityValues(entityValues, reduce, 'desc');
  return sorted[0]?.entity ?? '';
};

/**
 * Returns sorted entity/value pairs for a metric (e.g. for TABLE helper).
 * Default reduce is 'sum', sort is desc (highest first).
 */
export const getSortedEntityValues = (
  extractionOptions: ExtractionOptions,
  dataset: DataProviderSet,
  reduce: AggregationFunction = 'sum',
  sortDirection: 'asc' | 'desc' = 'desc'
): EntityMetric[] => {
  const entityValues = entityValuesFromDataset({
    dataset,
    extractionOptions: {
      ...extractionOptions,
      entity: undefined,
      label: undefined,
    },
  });
  if (entityValues.length === 0) return [];
  return reduceAndSortEntityValues(entityValues, reduce, sortDirection);
};

/**
 * Returns the entity label with the minimum value for a metric.
 */
export const getArgMin = (
  extractionOptions: ExtractionOptions,
  dataset: DataProviderSet,
  reduce: AggregationFunction = 'min'
): string => {
  const entityValues = entityValuesFromDataset({
    dataset,
    extractionOptions: {
      ...extractionOptions,
      entity: undefined,
      label: undefined,
    },
  });

  if (entityValues.length === 0) return '';

  const sorted = reduceAndSortEntityValues(entityValues, reduce, 'asc');
  return sorted[0]?.entity ?? '';
};

/**
 * Returns the 1-based rank of an entity for a metric.
 * Ranking is by value DESC; ties are broken by entity label ASC.
 */
export const getRank = (
  entityParam: string,
  extractionOptions: ExtractionOptions,
  dataset: DataProviderSet,
  reduce: AggregationFunction = 'last'
): number | null => {
  const explicitEntity = (
    entityParam ||
    extractionOptions.entity ||
    extractionOptions.label ||
    ''
  ).trim();

  if (!explicitEntity) return null;

  const entityValues = entityValuesFromDataset({
    dataset,
    extractionOptions: {
      ...extractionOptions,
      entity: undefined,
      label: undefined,
    },
  });

  if (entityValues.length === 0) return null;

  const sorted = reduceAndSortEntityValues(entityValues, reduce, 'desc');
  const idx = sorted.findIndex((x) => matchesEntity(x, explicitEntity));
  return idx === -1 ? null : idx + 1;
};

export type MaxPoint = {
  date: string | null;
  value: number;
};

/**
 * Returns the maximum (date,value) point in a time series dataset (last point in case of ties).
 * If entity is specified, returns max for that entity; otherwise returns max across all entities.
 */
export const getMaxPoint = (
  extractionOptions: ExtractionOptions,
  dataset: DataProviderSet
): MaxPoint | null => {
  if (
    dataset.type !== DataType.TIMESERIES &&
    dataset.type !== DataType.BREAKDOWN_TIMESERIES
  ) {
    return null;
  }

  const normalizedPoints = normalizeDataset(dataset);
  const targetEntity = (
    extractionOptions.entity ||
    extractionOptions.label ||
    ''
  ).trim();

  let filtered = normalizedPoints;
  if (targetEntity) {
    filtered = filtered.filter((p) => matchesEntity(p, targetEntity));
  }

  if (extractionOptions.segment) {
    filtered = filtered.filter(
      (p) => p.segment && p.segment === extractionOptions.segment
    );
  }

  let best: MaxPoint | null = null;
  filtered.forEach((p) => {
    const value = p.value;
    if (value === null || !Number.isFinite(value)) return;

    const date = p.date ?? null;

    if (best === null) {
      best = { date, value };
      return;
    }

    if (value > best.value) {
      best = { date, value };
      return;
    }

    if (
      value === best.value &&
      date &&
      (!best.date || date.localeCompare(best.date) > 0)
    ) {
      best = { date, value };
    }
  });

  return best;
};

/**
 * Returns the entity label with the maximum point across all entities in a time series.
 */
export const getMaxPointEntity = (
  extractionOptions: ExtractionOptions,
  dataset: DataProviderSet
): string => {
  if (
    dataset.type !== DataType.TIMESERIES &&
    dataset.type !== DataType.BREAKDOWN_TIMESERIES
  ) {
    return '';
  }

  const normalizedPoints = normalizeDataset(dataset);

  let filtered = normalizedPoints;

  if (extractionOptions.segment) {
    filtered = filtered.filter(
      (p) => p.segment && p.segment === extractionOptions.segment
    );
  }

  const entityMaxPoints = new Map<string, MaxPoint>();

  filtered.forEach((p) => {
    const value = p.value;
    if (value === null || !Number.isFinite(value)) return;

    const entity = p.entity;
    const date = p.date ?? null;

    const existing = entityMaxPoints.get(entity);
    if (!existing) {
      entityMaxPoints.set(entity, { date, value });
      return;
    }

    if (value > existing.value) {
      entityMaxPoints.set(entity, { date, value });
      return;
    }

    if (
      value === existing.value &&
      date &&
      (!existing.date || date.localeCompare(existing.date) > 0)
    ) {
      entityMaxPoints.set(entity, { date, value });
    }
  });

  if (entityMaxPoints.size === 0) return '';

  let bestEntity = '';
  let bestPoint: MaxPoint | null = null;

  entityMaxPoints.forEach((point, entity) => {
    if (bestPoint === null) {
      bestPoint = point;
      bestEntity = entity;
      return;
    }

    if (point.value > bestPoint.value) {
      bestPoint = point;
      bestEntity = entity;
      return;
    }

    if (point.value === bestPoint.value) {
      const pointDate = point.date ?? '';
      const bestDate = bestPoint.date ?? '';
      if (pointDate > bestDate) {
        bestPoint = point;
        bestEntity = entity;
      } else if (
        pointDate === bestDate &&
        entity.localeCompare(bestEntity) < 0
      ) {
        bestPoint = point;
        bestEntity = entity;
      }
    }
  });

  return bestEntity;
};

export type MinPoint = {
  date: string | null;
  value: number;
};

/**
 * Returns the minimum (date,value) point in a time series dataset (first point in case of ties).
 * If entity is specified, returns min for that entity; otherwise returns min across all entities.
 */
export const getMinPoint = (
  extractionOptions: ExtractionOptions,
  dataset: DataProviderSet
): MinPoint | null => {
  if (
    dataset.type !== DataType.TIMESERIES &&
    dataset.type !== DataType.BREAKDOWN_TIMESERIES
  ) {
    return null;
  }

  const normalizedPoints = normalizeDataset(dataset);
  const targetEntity = (
    extractionOptions.entity ||
    extractionOptions.label ||
    ''
  ).trim();

  let filtered = normalizedPoints;
  if (targetEntity) {
    filtered = filtered.filter((p) => matchesEntity(p, targetEntity));
  }

  if (extractionOptions.segment) {
    filtered = filtered.filter(
      (p) => p.segment && p.segment === extractionOptions.segment
    );
  }

  let best: MinPoint | null = null;
  filtered.forEach((p) => {
    const value = p.value;
    if (value === null || !Number.isFinite(value)) return;

    const date = p.date ?? null;

    if (best === null) {
      best = { date, value };
      return;
    }

    if (value < best.value) {
      best = { date, value };
      return;
    }

    if (
      value === best.value &&
      date &&
      (!best.date || date.localeCompare(best.date) < 0)
    ) {
      best = { date, value };
    }
  });

  return best;
};

/**
 * Returns the entity label with the minimum point across all entities in a time series.
 */
export const getMinPointEntity = (
  extractionOptions: ExtractionOptions,
  dataset: DataProviderSet
): string => {
  if (
    dataset.type !== DataType.TIMESERIES &&
    dataset.type !== DataType.BREAKDOWN_TIMESERIES
  ) {
    return '';
  }

  const normalizedPoints = normalizeDataset(dataset);

  let filtered = normalizedPoints;

  if (extractionOptions.segment) {
    filtered = filtered.filter(
      (p) => p.segment && p.segment === extractionOptions.segment
    );
  }

  const entityMinPoints = new Map<string, MinPoint>();

  filtered.forEach((p) => {
    const value = p.value;
    if (value === null || !Number.isFinite(value)) return;

    const entity = p.entity;
    const date = p.date ?? null;

    const existing = entityMinPoints.get(entity);
    if (!existing) {
      entityMinPoints.set(entity, { date, value });
      return;
    }

    if (value < existing.value) {
      entityMinPoints.set(entity, { date, value });
      return;
    }

    if (
      value === existing.value &&
      date &&
      (!existing.date || date.localeCompare(existing.date) < 0)
    ) {
      entityMinPoints.set(entity, { date, value });
    }
  });

  if (entityMinPoints.size === 0) return '';

  let bestEntity = '';
  let bestPoint: MinPoint | null = null;

  entityMinPoints.forEach((point, entity) => {
    if (bestPoint === null) {
      bestPoint = point;
      bestEntity = entity;
      return;
    }

    if (point.value < bestPoint.value) {
      bestPoint = point;
      bestEntity = entity;
      return;
    }

    if (point.value === bestPoint.value) {
      const pointDate = point.date ?? '';
      const bestDate = bestPoint.date ?? '';
      if (pointDate < bestDate) {
        bestPoint = point;
        bestEntity = entity;
      } else if (
        pointDate === bestDate &&
        entity.localeCompare(bestEntity) < 0
      ) {
        bestPoint = point;
        bestEntity = entity;
      }
    }
  });

  return bestEntity;
};

export const percentileRankFromSorted = (
  sorted: number[],
  targetValue: number,
  order: 'asc' | 'desc' = 'desc'
): number => {
  if (sorted.length === 0) return 0;
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const midValue = sorted[mid];
    if (midValue !== undefined && midValue <= targetValue) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  const rank = lo / sorted.length;
  return order === 'asc' ? 1 - rank : rank;
};

export const buildPercentileRankDistribution = (
  targetEntity: string,
  extractionOptions: ExtractionOptions,
  dataset: DataProviderSet,
  reduce: AggregationFunction
): { sortedDistribution: number[]; targetValue: number } | null => {
  const fullOptions: ExtractionOptions = {
    ...extractionOptions,
    entity: undefined,
    label: undefined,
    timeAggregation: 'all',
  };

  const allValues = extractDatasetValues(dataset, fullOptions);
  const numericValues = allValues.filter(
    (v): v is number => v !== null && Number.isFinite(v)
  );

  if (numericValues.length === 0) return null;

  const targetOptions: ExtractionOptions = {
    ...extractionOptions,
    entity: targetEntity,
    label: undefined,
    timeAggregation: 'all',
  };

  const targetValues = extractDatasetValues(dataset, targetOptions);
  const targetValue = reduceNumericValues(targetValues, reduce);

  if (targetValue === null || !Number.isFinite(targetValue)) return null;

  const sortedDistribution = [...numericValues].sort((a, b) => a - b);
  return { sortedDistribution, targetValue };
};

export const getPercentileRank = (
  extractionOptions: ExtractionOptions,
  dataset: DataProviderSet,
  reduce: AggregationFunction = 'avg',
  order: 'asc' | 'desc' = 'desc'
): number | null => {
  const targetEntity = (
    extractionOptions.entity ||
    extractionOptions.label ||
    ''
  ).trim();

  if (!targetEntity) return null;

  if (
    dataset.type === DataType.TIMESERIES ||
    dataset.type === DataType.BREAKDOWN_TIMESERIES
  ) {
    const entityValues = entityValuesFromDataset({
      dataset,
      extractionOptions: {
        ...extractionOptions,
        entity: undefined,
        label: undefined,
      },
    });

    if (entityValues.length === 0) return null;

    const entityMetrics: number[] = [];
    let targetValue: number | null = null;

    for (const { entity, id, values } of entityValues) {
      const reducedValue = reduceNumericValues(values, reduce);
      if (reducedValue === null || !Number.isFinite(reducedValue)) continue;

      entityMetrics.push(reducedValue);

      if (matchesEntity({ entity, id }, targetEntity)) {
        targetValue = reducedValue;
      }
    }

    if (targetValue === null || entityMetrics.length === 0) return null;

    const sorted = [...entityMetrics].sort((a, b) => a - b);
    return percentileRankFromSorted(sorted, targetValue, order);
  }

  const built = buildPercentileRankDistribution(
    targetEntity,
    extractionOptions,
    dataset,
    reduce
  );

  if (!built) return null;

  return percentileRankFromSorted(
    built.sortedDistribution,
    built.targetValue,
    order
  );
};

/**
 * Step 1: Time-Weighted Average
 * Calculates the weighted average using a step-function over the last 24 months of data.
 * This logic mirrors the back-end weight distribution:
 * - M0 (Most Recent) to M11 (12 months ago): Weight = 0.6
 * - M12 to M17 (18 months ago): Weight = 0.3
 * - M18 to M23 (24 months ago): Weight = 0.1
 *
 * NOTE: The input 'values' are assumed to be the final monthly data points (e.g.,
 * the 12-month rolling average from the SQL output) for the last 24 months.
 */
export const calculateTimeWeightedScore = (
  values: { date: string; value: number }[]
): number | null => {
  const sorted = [...values].sort((a, b) => a.date.localeCompare(b.date));
  const validPoints = sorted.filter((p) => Number.isFinite(p.value)).slice(-24);

  if (validPoints.length === 0) return null;

  let totalWeightedValue = 0;
  let totalWeight = 0;
  const N = validPoints.length;

  validPoints.forEach((point, index) => {
    const recencyIndex = N - 1 - index;
    let weight = 0;

    if (recencyIndex >= 0 && recencyIndex <= 11) {
      weight = 0.6;
    } else if (recencyIndex >= 12 && recencyIndex <= 17) {
      weight = 0.3;
    } else if (recencyIndex >= 18 && recencyIndex <= 23) {
      weight = 0.1;
    }

    if (weight > 0) {
      totalWeightedValue += point.value * weight;
      totalWeight += weight;
    }
  });

  return totalWeight === 0 ? null : totalWeightedValue / totalWeight;
};

/**
 * Step 2 & 3: Multi-Metric Composite Rank
 * Accepts multiple timeseries datasets. Per metric per entity:
 * - reduce="time_weighted_avg": uses step-function weights over last 24 months
 * - reduce="avg"|"median"|etc: uses reduceNumericValues over all values
 * Then overall score = mean of per-metric scores per entity, and returns
 * the target entity's percentile (rank index / N) where rank 1 = lowest overall score.
 */
export const getCompositePercentileRank = (
  targetEntity: string,
  datasets: DataProviderSet[],
  extractionOptions: ExtractionOptions,
  reduce: AggregationFunction = 'avg'
): number | null => {
  const entityScoresMap = new Map<string, number[]>();
  const entityInfoByKey = new Map<string, { entity: string; id?: string }>();

  datasets.forEach((dataset) => {
    if (
      dataset.type !== DataType.TIMESERIES &&
      dataset.type !== DataType.BREAKDOWN_TIMESERIES
    ) {
      return;
    }

    const normalized = normalizeDataset(dataset);
    const groupedByEntity = new Map<
      string,
      { date: string; value: number }[]
    >();

    normalized.forEach((pt) => {
      if (
        extractionOptions.segment &&
        pt.segment !== extractionOptions.segment
      ) {
        return;
      }
      const key = entityKey(pt);
      if (!groupedByEntity.has(key)) {
        groupedByEntity.set(key, []);
        entityInfoByKey.set(key, { entity: pt.entity, id: pt.id });
      }
      if (pt.value !== null) {
        groupedByEntity.get(key)?.push({
          date: pt.date ?? '',
          value: pt.value,
        });
      }
    });

    groupedByEntity.forEach((points, entity) => {
      const score =
        reduce === 'time_weighted_avg'
          ? calculateTimeWeightedScore(points)
          : reduceNumericValues(
              points.map((p) => p.value),
              reduce
            );
      if (score !== null) {
        if (!entityScoresMap.has(entity)) entityScoresMap.set(entity, []);
        entityScoresMap.get(entity)?.push(score);
      }
    });
  });

  const finalEntityScores: number[] = [];
  let targetScore: number | null = null;

  entityScoresMap.forEach((scores, entity) => {
    const sum = scores.reduce((a, b) => a + b, 0);
    const avg = sum / scores.length;
    finalEntityScores.push(avg);

    const info = entityInfoByKey.get(entity);
    if (info && matchesEntity(info, targetEntity)) {
      targetScore = avg;
    }
  });

  if (targetScore === null || finalEntityScores.length < 2) return null;

  const targetValue = targetScore;

  const sortedDistribution = [...finalEntityScores].sort((a, b) => a - b);
  const N = sortedDistribution.length;
  const L = sortedDistribution.filter((s) => s < targetValue).length;
  const F = sortedDistribution.filter((s) => s === targetValue).length;

  return (L + 0.5 * F) / N;
};
